"use client";

import React, { useMemo } from "react";
import { DetailNavigationProvider } from "@/contexts/DetailNavigationContext";
import { FilterContextProvider } from "@/contexts/FilterContext";
import type { BaseEntity } from "./UniversalCRMTable/types";
import type { UniversalEntityPageProps } from "./UniversalEntityPage/types";
import { useSavedViews } from "@/lib/hooks/api/saved-views";
import { savedViewToConfig } from "@/lib/hooks/api/saved-views";
import { useRegistryConfigEnhancer } from "@/lib/entity-registry";

// Extracted hooks
import { useUniversalEntityPageAuth } from "./UniversalEntityPage/hooks/useUniversalEntityPageAuth";
import { useUniversalEntityPageComposition } from "./UniversalEntityPage/hooks/useUniversalEntityPageComposition";
import { useAuthGuardDecision } from "./auth/useAuthGuardDecision";

// Extracted components
import { UniversalEntityPageStates } from "./UniversalEntityPage/components/UniversalEntityPageStates";
import { UniversalEntityPageContent } from "./UniversalEntityPage/components/UniversalEntityPageContent";
import { UniversalEntityPageContextProvider } from "./UniversalEntityPage/contexts/UniversalEntityPageContext";

/**
 * Authenticated inner component: only mounted when we have an app user.
 * Runs useUniversalEntityPageComposition (all hooks) so hook count is stable.
 *
 * DetailNavigationProvider must wrap this component so useDetailNavigation
 * is available when useUniversalEntityPageComposition is called.
 *
 * FilterContextProvider must wrap this component so useFilterContext
 * is available when useUniversalEntityPageComposition calls useUniversalEntityPageData.
 *
 * Memoized to prevent remounting when parent re-renders but props haven't changed.
 * This is critical for preventing full page remounts during search/filter operations.
 */
function UniversalEntityPageAuthenticatedComponent<T extends BaseEntity>({
  props,
  allViews,
  entityName,
}: {
  props: UniversalEntityPageProps<T>;
  allViews: Array<{ id: string; name: string; filters?: Record<string, any> }>;
  entityName: string;
}) {
  const contextValue = useUniversalEntityPageComposition(props);

  // Only show full-page loading on initial load (before any data arrives)
  // Once data has loaded once, always show content with loading overlay
  // This prevents the entire page from disappearing during search/filter updates
  const hasLoadedOnce = contextValue.dataState.hasLoadedOnce;
  const showStates = contextValue.dataState.isLoading && !hasLoadedOnce;

  if (showStates) {
    return (
      <UniversalEntityPageStates
        authLoading={contextValue.auth.authLoading}
        isAuthenticated={contextValue.auth.isAuthenticated}
        isEmployee={contextValue.auth.isEmployee}
        isAdmin={contextValue.auth.isAdmin}
        isClientUser={contextValue.auth.isClientUser}
        isLoading={contextValue.dataState.isLoading}
        data={contextValue.dataState.data}
        config={props.config}
      />
    );
  }

  // Always render content after initial load
  // The table will show a loading overlay instead of disappearing
  return (
    <UniversalEntityPageContextProvider value={contextValue}>
      <UniversalEntityPageContent {...props} />
    </UniversalEntityPageContextProvider>
  );
}

const UniversalEntityPageAuthenticated = React.memo(
  UniversalEntityPageAuthenticatedComponent
) as typeof UniversalEntityPageAuthenticatedComponent;

/**
 * UniversalEntityPage - Main orchestrator component
 *
 * This component handles auth state internally and gates rendering on appUser existence.
 * It does NOT perform redirects - those are handled by middleware/page-level guards.
 * This prevents race conditions where authenticated users see "Redirecting to login..." messages.
 *
 * The component runs the full composition hook to manage data loading and rendering.
 */
export function UniversalEntityPage<T extends BaseEntity>(props: UniversalEntityPageProps<T>) {
  const auth = useUniversalEntityPageAuth();

  // Perform the actual login redirect when auth has resolved and the user is
  // unauthenticated. Without this, the "Redirecting to login..." state below is
  // a dead end: middleware only redirects when NO auth cookies exist, so a user
  // arriving with stale/expired cookies is punted to the client and never moved.
  // No role flags are passed, so this redirects ONLY on !isAuthenticated —
  // authenticated employee/client access is unchanged (role gating stays in
  // middleware + page-level guards). Reuses the same cookie-aware decision logic
  // as AuthGuard/EmployeeGuard.
  useAuthGuardDecision({
    requireEmployee: false,
    requireAdmin: false,
    requireClient: false,
    requiredUserTypes: [],
    redirectTo: "/auth/employee",
  });

  // Fetch saved views from Supabase early so we can pass them to FilterContextProvider
  const { data: savedViews = [] } = useSavedViews(props.entityName);

  // Conditionally enhance config based on entity type
  // Only the relevant config enhancer hook is called for the current entity
  const enhancedConfig = useRegistryConfigEnhancer(props.entityName, props.config);

  // Memoize the props to avoid recreating the provider on every render
  // This must be called unconditionally to maintain hook order
  // Use enhancedConfig instead of props.config to include tag options
  const memoizedProps = useMemo(
    () => ({ ...props, config: enhancedConfig }),
    [props, enhancedConfig]
  );

  // Combine config views and Supabase views
  const allViews = useMemo(() => {
    const configViews = enhancedConfig.savedViews || [];
    const savedViewConfigs = savedViews.map(savedViewToConfig);
    return [...configViews, ...savedViewConfigs];
  }, [enhancedConfig.savedViews, savedViews]);

  // Show loading state while auth is being checked
  if (auth.authLoading) {
    return (
      <UniversalEntityPageStates
        authLoading
        isAuthenticated={false}
        isEmployee={false}
        isAdmin={false}
        isClientUser={false}
        isLoading
        data={[]}
        config={props.config}
      />
    );
  }

  // CRITICAL: Gate on appUser existence to prevent calling useUniversalEntityPageComposition without auth
  // This is NOT a redirect - it's a guard to prevent hook errors
  // Actual auth redirects are handled by middleware or page-level AuthGuard
  if (!auth.appUser) {
    return (
      <UniversalEntityPageStates
        authLoading={false}
        isAuthenticated={auth.isAuthenticated}
        isEmployee={auth.isEmployee}
        isAdmin={auth.isAdmin}
        isClientUser={auth.isClientUser}
        isLoading={false}
        data={[]}
        config={props.config}
      />
    );
  }

  return (
    <DetailNavigationProvider>
      <FilterContextProvider
        config={enhancedConfig as unknown as any}
        entityName={props.entityName}
        allViews={allViews}
      >
        <UniversalEntityPageAuthenticated<any>
          props={memoizedProps as any}
          allViews={allViews}
          entityName={props.entityName}
        />
      </FilterContextProvider>
    </DetailNavigationProvider>
  );
}
